import React, { useEffect, useMemo, useState } from "react";
import {
  Badge, Box, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Flex, HStack, Link,
  SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from "@chakra-ui/react";
import { ExternalLink } from "lucide-react";
import { Line } from "react-chartjs-2";
import { getMetricasAnuncio } from "../../services/api";
import { ErroCarga, dataHoraBR, diaBR, num } from "../SaudeAnunciosML/comum";
import { BadgeTipo, COR_CLASSICO, COR_PREMIUM, Cartao, brl, conversao, opcoesLinha, serieLinha } from "./base";

const MOTIVOS = {
  limite_79: "No Premium passaria de R$ 79 (frete grátis): fica Clássico.",
  tarifa_indisponivel: "Tarifa do ML indisponível agora.",
  sem_convergencia: "A tarifa muda de faixa nesse preço: não existe preço Premium com o mesmo lucro.",
  sem_categoria: "Categoria do anúncio desconhecida.",
};

const pctTarifa = (v) => (v === null || v === undefined ? "—" : `${num(Number(v) * 100, 2).replace(/,00$/, "")}%`);

function Info({ rotulo, children }) {
  return (
    <Box minW={0}>
      <Text fontSize="2xs" color="gray.600" textTransform="uppercase" fontWeight="bold" letterSpacing="wide">{rotulo}</Text>
      <Box fontSize="sm" fontWeight="semibold" color="gray.800" mt={0.5}>{children}</Box>
    </Box>
  );
}

function Regra({ regra }) {
  if (!regra) return <Text fontSize="sm" color="gray.600">Sem preço ou categoria para calcular.</Text>;
  if (regra.erro) return <Text fontSize="sm" color="orange.700">{regra.erro}</Text>;
  const premium = regra.tipo_atual === "gold_pro";
  return (
    <VStack align="stretch" spacing={2} fontSize="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
      <SimpleGrid columns={2} spacing={2}>
        <Info rotulo="Preço Clássico">{brl(regra.preco_classico)}</Info>
        <Info rotulo={premium ? "Preço Premium (no ML)" : "Preço se fosse Premium"}>
          {regra.convertido ? brl(regra.preco) : <Text as="span" color="gray.600">fica Clássico</Text>}
        </Info>
      </SimpleGrid>
      {regra.convertido ? (
        <Text fontSize="xs" color="gray.600">
          Tarifa {pctTarifa(regra.pct_classico)} no Clássico e {pctTarifa(regra.pct_premium)} no Premium (categoria {regra.categoria}),
          imposto {pctTarifa(regra.imposto)}, fator ×{num(regra.fator, 4)}. Lucro igual nos dois até R$ 0,01.
        </Text>
      ) : (
        <Text fontSize="xs" color="gray.600">{MOTIVOS[regra.motivo] || regra.motivo}</Text>
      )}
    </VStack>
  );
}

export default function DetalheAnuncio({ idMl, onFechar }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    let vivo = true;
    if (!idMl) return undefined;
    setDados(null);
    setErro(false);
    setCarregando(true);
    getMetricasAnuncio(idMl).then((r) => {
      if (!vivo) return;
      setCarregando(false);
      setErro(!r);
      setDados(r);
    });
    return () => { vivo = false; };
  }, [idMl]);

  const r = dados?.resumo;
  const serie = useMemo(() => dados?.serie || [], [dados]);
  const grafico = useMemo(() => ({
    labels: serie.map((s) => diaBR(s.data)),
    datasets: [
      serieLinha("Visitas", serie.map((s) => Number(s.visitas || 0)), COR_CLASSICO, { yAxisID: "y" }),
      serieLinha("Vendas (un.)", serie.map((s) => Number(s.unidades || 0)), COR_PREMIUM, { yAxisID: "y1" }),
    ],
  }), [serie]);
  const opcoes = useMemo(() => {
    const o = opcoesLinha();
    o.scales.y1 = { position: "right", beginAtZero: true, grid: { display: false }, border: { display: false }, ticks: { precision: 0, font: { size: 10 } } };
    return o;
  }, []);

  return (
    <Drawer isOpen={!!idMl} placement="right" size={{ base: "full", md: "lg" }} onClose={onFechar}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pr={12}>
          <Text fontSize="md" noOfLines={2}>{r?.nome || idMl}</Text>
          <HStack spacing={2} mt={1} flexWrap="wrap" fontWeight="normal">
            <Link href={`https://produto.mercadolivre.com.br/MLB-${String(idMl || "").replace("MLB", "")}`} isExternal fontSize="sm" color="blue.600">
              {idMl} <ExternalLink size={12} style={{ display: "inline" }} />
            </Link>
            {r && <Text fontSize="sm" color="gray.600">Conta {r.conta_id} · SKU {r.produto_id ?? "—"}</Text>}
            {r && <BadgeTipo tipo={r.listing_type_id} />}
            {r?.status === "paused" && <Badge colorScheme="orange">Pausado</Badge>}
          </HStack>
        </DrawerHeader>
        <DrawerBody py={4} bg="gray.50">
          {carregando && <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>}
          {erro && !carregando && <ErroCarga titulo="Não foi possível carregar o anúncio." />}
          {r && (
            <VStack align="stretch" spacing={4}>
              <Cartao titulo="Preço e regra">
                <Info rotulo="Preço atual no ML">{brl(r.preco)}</Info>
                <Box mt={3}><Regra regra={dados.regra} /></Box>
              </Cartao>

              <Cartao titulo="Últimos 30 dias">
                <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={3}>
                  <Info rotulo="Visitas">{num(r.visitas_30)}</Info>
                  <Info rotulo="Vendas (un.)">{num(r.unidades_30)}</Info>
                  <Info rotulo="Conversão">{conversao(r.pedidos_30, r.visitas_30)}</Info>
                  <Info rotulo="Receita">{brl(r.receita_30, 0)}</Info>
                  <Info rotulo="Lucro">{brl(r.lucro_30, 0)}</Info>
                  <Info rotulo="Última venda">{r.ultima_venda ? diaBR(r.ultima_venda) : "—"}</Info>
                </SimpleGrid>
              </Cartao>

              <Cartao titulo="Visitas e vendas por dia (90 dias)">
                {!serie.length ? <Text fontSize="sm" color="gray.600">Sem visitas ou vendas registradas.</Text> : (
                  <Box h="220px"><Line data={grafico} options={opcoes} role="img" aria-label="Visitas e vendas por dia do anúncio" /></Box>
                )}
              </Cartao>

              <Cartao titulo="Mesmo produto na outra conta">
                {!dados.par?.length ? <Text fontSize="sm" color="gray.600">Sem anúncio na outra conta.</Text> : (
                  <Box overflowX="auto">
                    <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      <Thead><Tr><Th>Anúncio</Th><Th>Tipo</Th><Th isNumeric>Preço</Th><Th isNumeric>Visitas 30d</Th><Th isNumeric>Vendas 30d</Th><Th isNumeric>Lucro 30d</Th></Tr></Thead>
                      <Tbody>
                        {dados.par.map((p) => (
                          <Tr key={p.id_ml}>
                            <Td whiteSpace="nowrap">{p.id_ml}<Text fontSize="xs" color="gray.600">Conta {p.conta_id}</Text></Td>
                            <Td><BadgeTipo tipo={p.listing_type_id} /></Td>
                            <Td isNumeric>{brl(p.preco)}</Td>
                            <Td isNumeric>{num(p.visitas_30)}</Td>
                            <Td isNumeric>{num(p.unidades_30)}</Td>
                            <Td isNumeric>{brl(p.lucro_30, 0)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Cartao>

              <Cartao titulo="Histórico de trocas">
                {!dados.trocas?.length ? <Text fontSize="sm" color="gray.600">Nenhuma troca de tipo registrada.</Text> : (
                  <VStack align="stretch" spacing={2}>
                    {dados.trocas.map((t, i) => (
                      <Box key={i} fontSize="sm" p={2} borderWidth="1px" borderColor="gray.100" borderRadius="md" bg="white">
                        <Flex gap={2} align="center" flexWrap="wrap">
                          <BadgeTipo tipo={t.tipo_antes} /> <Text as="span">→</Text> <BadgeTipo tipo={t.tipo_depois} />
                          <Badge colorScheme={t.status === "ok" ? "green" : "red"}>{t.status}</Badge>
                          <Text fontSize="xs" color="gray.600" ml="auto">{dataHoraBR(t.created_at)}</Text>
                        </Flex>
                        <Text fontSize="xs" color="gray.700" mt={1}>
                          Preço {brl(t.preco_antes)} → {t.preco_depois !== null ? brl(t.preco_depois) : "—"} (Clássico {brl(t.preco_classico)}
                          {t.pct_premium ? `, tarifa ${pctTarifa(t.pct_classico)} → ${pctTarifa(t.pct_premium)}` : ""})
                        </Text>
                        {t.erro && <Text fontSize="xs" color="red.700" mt={1}>{t.erro}</Text>}
                      </Box>
                    ))}
                  </VStack>
                )}
              </Cartao>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
